import type { Result } from './result';
import sleep from './sleep';

const DEFAULT_MAX_BACKOFF = 60_000;

export type RetryOptions<T> = {
  /**
   * Function to determine if a retry should occur. Return true to retry, or throw to override the error.
   * If maxRetries is reached, the error in result will be rethrown to the caller.
   * Throwing from this function overrides the error in result.
   */
  shouldRetry?: (args: { result: Result<T>; attempt: number }) => boolean;
  /**
   * Optional function to calculate the delay before the next retry.
   * Receives the default exponential backoff delay. If not provided, defaultDelay is used.
   */
  calculateDelay?: (args: {
    result: Result<T>;
    attempt: number;
    defaultDelay: number;
  }) => number;
  /**
   * Maximum number of retry attempts allowed.
   */
  maxRetries: number;
  /**
   * Maximum delay in milliseconds between retries. Defaults to 60,000 ms (60 seconds).
   */
  maxBackOff?: number;
  /**
   * Optional AbortSignal to support aborting between retries.
   */
  abortSignal?: AbortSignal;
};

export class AbortRetryError extends Error {
  constructor() {
    super('Retry Aborted');
  }
}

export class RetryError extends Error {
  constructor() {
    super('Retry Error');
  }
}

const delay = async <T>(
  result: Result<T>,
  attempt: number,
  options: RetryOptions<T>,
) => {
  const {
    calculateDelay,
    maxBackOff = DEFAULT_MAX_BACKOFF,
    abortSignal,
  } = options;
  const noise = Math.random() * 100;
  const defaultDelay = Math.min(2 ** (attempt - 1) * 1000, maxBackOff) + noise;
  const delayValue =
    calculateDelay?.({
      defaultDelay,
      result,
      attempt,
    }) ?? defaultDelay;
  await sleep(delayValue, { abortSignal }).catch(() => {
    throw new AbortRetryError();
  });
};

const tryCall = async <T>(
  fn: () => Promise<T>,
  attempt: number,
  options: RetryOptions<T>,
): Promise<Result<T>> => {
  const result: Result<T> = { ok: true, value: await fn() };

  // Give the caller the chance to retry successful calls as well
  try {
    if (options.shouldRetry?.({ result, attempt })) {
      throw new RetryError();
    }
  } catch (error) {
    return { ok: false, error };
  }

  return result;
};

/**
 * Executes an async function with automatic retries using exponential backoff.
 *
 * Retries the provided async function if it fails or if the `shouldRetry` option returns true.
 * The delay between retries increases exponentially with optional random noise, and can be customized.
 *
 * - The retry loop stops and throws the last error if the maximum number of retries is reached,
 *   or if `shouldRetry` returns false.
 * - If the provided AbortSignal is triggered, an AbortRetryError is thrown.
 * - The delay between retries is calculated as `Math.min(2 ** (attempt - 1) * 1000, maxBackOff) + noise` ms,
 *   unless overridden by the `calculateDelay` option.
 *
 * @param fn The async function to execute and retry on failure.
 * @param options RetryOptions controlling retry logic, delay, and abort behavior.
 * @returns The resolved value from the async function if successful.
 * @throws {AbortRetryError} If the operation is aborted via AbortSignal.
 * @throws {Error} If all retries fail or if `shouldRetry` throws.
 */
const retryWithExponentialBackOff = async <T>(
  fn: () => Promise<T>,
  options: RetryOptions<T>,
): Promise<T> => {
  const { shouldRetry, maxRetries, abortSignal } = options;

  let attempt = 1;
  while (true) {
    if (abortSignal?.aborted) {
      throw new AbortRetryError();
    }

    let retry: boolean;
    // The reason we use a result here is that clients are able to retry successful calls as well as failed
    // If options.shouldRetry throws an error in tryCall we don't want to pass that error to shouldRetry again
    // in the catch below. So in that case we return a Result with ok = false. This result is then handled
    // outside the catch below.
    let result: Result<T>;

    try {
      result = await tryCall(fn, attempt, options);
      retry = !result.ok;
    } catch (error) {
      result = { ok: false, error };
      try {
        // Throwing from shouldRetry means to retry but with an overridden error
        retry = shouldRetry?.({ result, attempt }) ?? true;
      } catch (shouldRetryError) {
        retry = true;
        result = { ok: false, error: shouldRetryError };
      }
    }

    if (result.ok) {
      return result.value;
    }

    // If we have reached the max number of retries or the caller does not want to retry
    // then throw the error that we caught to end the retry loop
    if (attempt >= maxRetries || !retry) {
      throw result.error;
    }

    await delay(result, attempt, options);
    attempt += 1;
  }
};

export default retryWithExponentialBackOff;
